import { useTranslation } from "react-i18next";
import {
  CloudOff,
  Droplets,
  RefreshCcw,
  Thermometer,
  Wind,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWeather } from "./use-weather";

const DetailRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Wind;
  label: string;
  value: string;
}) => (
  <div className="flex items-center justify-between gap-6 text-xs">
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <Icon className="size-3.5" aria-hidden />
      {label}
    </span>
    <span className="font-medium tabular-nums text-foreground">{value}</span>
  </div>
);

export const Weather = () => {
  const { t } = useTranslation("my-space");
  const {
    status,
    iconSrc,
    description,
    condition,
    place,
    temp,
    feelsLike,
    tempMin,
    tempMax,
    humidity,
    windKph,
    refetch,
  } = useWeather();

  if (status === "unavailable") {
    return (
      <div className="flex items-center gap-2">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/60">
          <CloudOff className="size-4 text-primary-strong/60" aria-hidden />
        </div>
        <div className="flex flex-col items-start leading-tight">
          <span className="text-xs font-medium text-primary-strong">
            {t("labels.weather_unavailable")}
          </span>
          <button
            type="button"
            onClick={refetch}
            className="flex items-center gap-1 text-[11px] text-primary-strong/65 underline-offset-2 hover:text-primary-strong hover:underline focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden"
          >
            <RefreshCcw className="size-2.5" aria-hidden />
            {t("ui.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 sm:gap-2.5">
        <Skeleton className="size-10 rounded-2xl bg-primary/10 sm:size-12 lg:size-14" />
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-5 w-12 bg-primary/10 sm:w-14" />
          <Skeleton className="h-2.5 w-20 bg-primary/10 sm:w-28" />
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Focusable so the detail panel is reachable without a pointer. */}
          <button
            type="button"
            aria-label={
              place
                ? t("labels.weather_aria_with_place", {
                    temp,
                    condition: condition ?? t("labels.current_conditions"),
                    place,
                  })
                : t("labels.weather_aria", {
                    temp,
                    condition: condition ?? t("labels.current_conditions"),
                  })
            }
            className="flex cursor-default items-center gap-2 rounded-xl text-start focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden sm:gap-2.5"
          >
            {/* Meteocons' `fill` set has white/light-grey cloud bodies — they read on a
                dark surface but wash out on the pastel hero, so give them an edge. */}
            <img
              className="size-10 shrink-0 [filter:drop-shadow(0_1px_1.5px_rgba(35,20,80,0.28))] sm:size-12 lg:size-14"
              alt={description ?? t("toasts.weather")}
              src={iconSrc}
            />
            {/* One dark hue stepped down by opacity, rather than dropping to a mid-grey —
                `muted-foreground` sits too close to the background's lightness here. */}
            <div className="flex min-w-0 flex-col items-start leading-tight">
              <span className="text-xl font-semibold tabular-nums text-primary-strong sm:text-2xl">
                {temp}°
              </span>
              <span className="text-xs font-medium text-primary-strong/75">
                {condition}
              </span>
              {place && (
                <span className="max-w-[14ch] truncate text-[11px] text-primary-strong/60 sm:max-w-[18ch]">
                  {place}
                </span>
              )}
            </div>
          </button>
        </TooltipTrigger>

        <TooltipContent side="bottom" align="end" className="px-3 py-2.5">
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-foreground capitalize">
              {description ?? condition}
            </p>
            <DetailRow
              icon={Thermometer}
              label={t("labels.feels_like")}
              value={`${feelsLike}°C`}
            />
            <DetailRow
              icon={Thermometer}
              label={t("labels.high_low")}
              value={`${tempMax}° / ${tempMin}°`}
            />
            <DetailRow
              icon={Droplets}
              label={t("labels.humidity")}
              value={`${humidity}%`}
            />
            <DetailRow
              icon={Wind}
              label={t("labels.wind")}
              value={`${windKph} km/h`}
            />
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
